/**
 * Classifies a resolved (HTTP 200) `POST /smart-fill/auto` response against
 * the specific dates a caller actually requested, so no UI surface has to
 * invent its own meaning for `success`/`has_changes`/`daily_results`.
 *
 * The bug this exists to prevent (live pre-launch QA, PlanningHub ->
 * Regenerate on a day with two protected linked rows): the backend can
 * legitimately respond with `success: true` AND `has_changes: true` while a
 * requested date is ALSO listed in `incomplete_dates` — `has_changes` is
 * set purely from whether any non-kept PENDING row exists that would be
 * removed on confirm, independent of whether the planner generated a
 * complete replacement for that date (see server.py smart_fill_auto:
 * `"has_changes": bool(any(not p.get("is_kept") for p in added_plans) or
 * any_removable_pending)`). A caller that gates only on
 * `success && has_changes` can therefore navigate into a review screen
 * showing only the protected/kept rows for a day with no real alternative,
 * looking exactly like the day was successfully regenerated.
 *
 * This classifier is the single place that decides "is this response
 * actually reviewable for the dates I asked about" — it never treats
 * `has_changes` alone as proof a complete plan exists.
 *
 * Returns one of:
 *   { outcome: 'reviewable' }
 *     Every requested date is absent from incomplete_dates AND there is a
 *     real change to show. Safe to navigate to /plan/review.
 *   { outcome: 'no_valid_plan', incompleteDates, reasonsByDate }
 *     At least one requested date has no complete replacement. The caller
 *     must NOT navigate anywhere and must NOT apply anything from this
 *     response — the existing saved/draft day is left exactly as it was.
 *   { outcome: 'no_changes' }
 *     Every requested date is complete, but nothing needed to change
 *     (already fits / nothing to add or remove).
 *   { outcome: 'failed' }
 *     The top-level request itself did not succeed.
 */
export function classifyAutoRegenResult(data, requestedDates) {
  var dates = requestedDates || [];

  if (!data || !data.success) {
    return { outcome: 'failed', incompleteDates: [] };
  }

  var incompleteSet = {};
  (data.incomplete_dates || []).forEach(function (d) { incompleteSet[d] = true; });
  var incompleteRequested = dates.filter(function (d) { return !!incompleteSet[d]; });

  if (incompleteRequested.length > 0) {
    var dailyResults = data.daily_results || [];
    var reasonsByDate = {};
    incompleteRequested.forEach(function (d) {
      var entry = dailyResults.filter(function (r) { return r && r.date === d; })[0];
      reasonsByDate[d] = (entry && entry.reason) || null;
    });
    return { outcome: 'no_valid_plan', incompleteDates: incompleteRequested, reasonsByDate: reasonsByDate };
  }

  var hasChanges = (data.has_changes !== undefined) ? data.has_changes : (data.meals_added > 0);
  if (!hasChanges) {
    return { outcome: 'no_changes', incompleteDates: [] };
  }

  return { outcome: 'reviewable', incompleteDates: [] };
}

/** Picks one representative reason out of a classification's
 * reasonsByDate (first requested incomplete date that has one), for
 * building a single toast message when multiple dates were requested. */
export function firstIncompleteReason(classification) {
  if (!classification || !classification.incompleteDates || !classification.reasonsByDate) {
    return null;
  }
  for (var i = 0; i < classification.incompleteDates.length; i++) {
    var reason = classification.reasonsByDate[classification.incompleteDates[i]];
    if (reason) return reason;
  }
  return null;
}

/** Informational copy for a 'no_changes' outcome (zero new meals, nothing to
 * remove). "No room" covers the legacy `retained_only_no_room` reason and the
 * planner's current `constrained_no_feasible_addition` (logged/kept food makes
 * any addition unsuitable); anything else (`all_kept`,
 * `retained_only_no_improvement`, none) means the day already fits. */
export function retainedOnlyMessage(dailyResults) {
  var noRoom = (dailyResults || []).some(function (d) {
    var r = d && d.reason;
    return r === 'retained_only_no_room' || r === 'constrained_no_feasible_addition';
  });
  return noRoom
    ? 'Your logged and kept meals leave no room for another suitable meal.'
    : "No additional meal improves this day's nutrition targets.";
}
